/** Resolves per-frame combat hit detection between player attacks and enemy skills. */
import Phaser from 'phaser';
import type { Player } from '../entities/Player';
import type { Enemy } from '../entities/Enemy';
import type { EnemyManager } from './EnemyManager';
import type { CombatSystem } from './CombatSystem';
import type { HitstopSystem } from './HitstopSystem';
import type { KnockbackSystem } from './KnockbackSystem';
import type { UpgradeEffectResolver } from './UpgradeEffectResolver';
import type { DamageNumbers } from './DamageNumbers';
import type { ScreenEffects } from './ScreenEffects';
import type { EnemyBuffManager } from './EnemyBuffManager';
import type { ProjectilePool } from './ProjectilePool';
import type { StatusEffectSystem } from './StatusEffectSystem';
import type { ZoneSystem } from './ZoneSystem';
import type { ChainTargetingSystem } from './ChainTargetingSystem';
import type { DeathTriggerSystem } from './DeathTriggerSystem';
import { normalize, distance, distanceSq } from '../utils/Math';
import { ConfigLoader } from './ConfigLoader';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugLog } from '../debug/DebugLog';
import { EnemyBlockSkillEffect } from '../skills/enemy/EnemyBlockSkillEffect';
import { COMBAT_ALERT_RADIUS, COMBAT_ALERT_AGGRO_AMOUNT } from '../ai/constants';
import { SimulationBridge } from '../simulation/SimulationBridge';

/** Enemy weights that qualify for the toughEnemyMultiplier XP bonus. */
const TOUGH_WEIGHTS = new Set(['heavy', 'immovable']);

interface CombatHitResolverDeps {
  player: Player;
  enemyManager: EnemyManager;
  combatSystem: CombatSystem;
  hitstopSystem: HitstopSystem;
  knockbackSystem: KnockbackSystem;
  upgradeResolver: UpgradeEffectResolver;
  damageNumbers: DamageNumbers;
  pushCrawl: (text: string, color: string) => void;
  screenEffects: ScreenEffects;
  buffManager: EnemyBuffManager;
  getDanger: () => number;
  // Shared attunement systems (ADR-064)
  statusEffectSystem: StatusEffectSystem;
  zoneSystem: ZoneSystem;
  chainTargetingSystem: ChainTargetingSystem;
  deathTriggerSystem: DeathTriggerSystem;
}

/** Checks player-vs-enemy and enemy-vs-player hit overlaps each frame, applying damage, XP, and knockback. */
export class CombatHitResolver {
  private player: Player;
  private enemyManager: EnemyManager;
  private combatSystem: CombatSystem;
  private hitstopSystem: HitstopSystem;
  private knockbackSystem: KnockbackSystem;
  private upgradeResolver: UpgradeEffectResolver;
  private damageNumbers: DamageNumbers;
  private pushCrawl: (text: string, color: string) => void;
  private screenEffects: ScreenEffects;
  private buffManager: EnemyBuffManager;
  private getDanger: () => number;
  private statusEffectSystem: StatusEffectSystem;
  private zoneSystem: ZoneSystem;
  private chainTargetingSystem: ChainTargetingSystem;
  private deathTriggerSystem: DeathTriggerSystem;

  // Swing-miss tracking: detect falling edge of hitbox lifecycle
  private wasHitboxActive = false;
  private swingHitCount = 0;
  private swingAttackType = '';

  /** Receive all combat subsystem dependencies and wire up swing-miss reset on player death. */
  constructor(deps: CombatHitResolverDeps) {
    this.player = deps.player;
    this.enemyManager = deps.enemyManager;
    this.combatSystem = deps.combatSystem;
    this.hitstopSystem = deps.hitstopSystem;
    this.knockbackSystem = deps.knockbackSystem;
    this.upgradeResolver = deps.upgradeResolver;
    this.damageNumbers = deps.damageNumbers;
    this.pushCrawl = deps.pushCrawl;
    this.screenEffects = deps.screenEffects;
    this.buffManager = deps.buffManager;
    this.getDanger = deps.getDanger;
    this.statusEffectSystem = deps.statusEffectSystem;
    this.zoneSystem = deps.zoneSystem;
    this.chainTargetingSystem = deps.chainTargetingSystem;
    this.deathTriggerSystem = deps.deathTriggerSystem;

    // Reset swing tracking on player death to prevent false MISS floaty
    this.player.events.on('entity-died', () => {
      this.wasHitboxActive = false;
    });
  }

  /** Resolve player melee hitbox overlaps against enemies, apply damage/stagger/XP with upgrade effects. */
  checkPlayerAttackHits(): void {
    const hitboxObj = this.player.hitbox.gameObject;
    const hitboxActive = hitboxObj !== null && hitboxObj.body !== null;

    // Falling edge: hitbox was active, now gone → swing ended
    if (this.wasHitboxActive && !hitboxActive) {
      if (this.swingHitCount === 0 && this.anyEnemyInMissRadius()) {
        const cfg = ConfigLoader.combat.damageNumbers;
        const skillId = ConfigLoader.getAttackConfig(this.swingAttackType)?.skillId ?? 'punch';
        const effect = this.player.skillActivation.getEffect(skillId);
        const label = effect?.missLabel ?? 'MISS';
        this.damageNumbers.spawnLabel(
          this.player.sprite.x, this.player.sprite.y,
          label, cfg.dealtColor, `${cfg.baseFontSizePx}px`,
        );
        const name = ConfigLoader.getV4Node(skillId)?.name ?? skillId;
        this.pushCrawl(`${label} (${name})`, '#aaaaaa');
      }
    }

    // Rising edge: hitbox just became active → new swing, reset counter
    if (!this.wasHitboxActive && hitboxActive) {
      this.swingHitCount = 0;
      this.swingAttackType = this.player.combat.attackType;
    }

    this.wasHitboxActive = hitboxActive;

    if (!hitboxObj) return;

    const hitBody = hitboxObj.body as Phaser.Physics.Arcade.Body;
    if (!hitBody) return;

    // Enforce per-swing target cap (not per-frame)
    const attackType = this.player.combat.attackType;
    const maxTargets = ConfigLoader.getAttackConfig(attackType)?.maxTargets;
    if (maxTargets !== undefined && this.swingHitCount >= maxTargets) return;

    const hitRect = new Phaser.Geom.Rectangle(hitBody.x, hitBody.y, hitBody.width, hitBody.height);
    const frozen = this.hitstopSystem.isFrozen(this.player);

    DebugLog.log('combat', `hitbox active | state=${this.player.combat.state} frozen=${frozen}`, {
      hitbox: { x: hitBody.x, y: hitBody.y, w: hitBody.width, h: hitBody.height },
    });
    const xpSkillId = ConfigLoader.getAttackConfig(attackType)?.skillId ?? 'punch';
    const playerBodyX = (this.player.sprite.body as Phaser.Physics.Arcade.Body).x;
    const playerBodyY = (this.player.sprite.body as Phaser.Physics.Arcade.Body).y;

    const candidates: { enemy: Enemy; distSq: number }[] = [];

    for (const enemy of this.enemyManager.all) {
      if (enemy.healthComponent.isDead) continue;

      const targetBody = enemy.sprite.body as Phaser.Physics.Arcade.Body;
      if (!targetBody) continue;

      if (this.player.hitbox.hasHit(enemy.sprite)) {
        DebugLog.log('combat', `skip ${enemy.config.id}: already hit`);
        continue;
      }

      const targetRect = new Phaser.Geom.Rectangle(targetBody.x, targetBody.y, targetBody.width, targetBody.height);
      const intersects = Phaser.Geom.Intersects.RectangleToRectangle(hitRect, targetRect);

      DebugLog.log('combat', `${enemy.config.id} intersect=${intersects}`, {
        target: { x: targetBody.x, y: targetBody.y, w: targetBody.width, h: targetBody.height },
      });

      if (intersects) {
        candidates.push({ enemy, distSq: distanceSq(playerBodyX, playerBodyY, targetBody.center.x, targetBody.center.y) });
      }
    }

    // Apply remaining target budget — sort by distance, slice to closest N
    const remaining = maxTargets !== undefined ? maxTargets - this.swingHitCount : undefined;
    if (remaining !== undefined && candidates.length > remaining) {
      candidates.sort((a, b) => a.distSq - b.distSq);
      candidates.length = remaining;
    }

    // Resolve upgrade effects for the XP skill (not the attack type — upgrades target the skill)
    const upgradeDamageMult = this.upgradeResolver.getResolvedDamageMultiplier(xpSkillId);
    const upgradeStagger = this.upgradeResolver.getStaggerEffect(xpSkillId);

    for (const { enemy } of candidates) {
      this.player.hitbox.recordHit(enemy.sprite);
      this.swingHitCount++;

      const hitDir = normalize(
        enemy.sprite.x - this.player.sprite.x,
        enemy.sprite.y - this.player.sprite.y
      );

      // Check for active block (Shield archetype) — reduce damage if frontal
      let blockMult: number | undefined;
      const activeSkill = enemy.ai.activeSkill;
      if (activeSkill instanceof EnemyBlockSkillEffect && activeSkill.isBlocking()) {
        blockMult = activeSkill.getBlockReduction(hitDir.x, hitDir.y);
      }

      // Combine block multiplier, upgrade damage multiplier, enemy vulnerability, and dash bonus
      const vulnMult = enemy.getVulnerabilityMultiplier();
      const dashMult = this.player.combat.isDashAttack ? ConfigLoader.movement.dash.dashAttackDamageMultiplier : 1;
      const combinedMult = (blockMult ?? 1) * upgradeDamageMult * vulnMult * dashMult;
      const effectiveMult = combinedMult === 1 ? undefined : combinedMult;

      const result = this.combatSystem.applyHit(
        this.player, enemy,
        attackType,
        hitDir,
        {
          godMode: DebugConfig.flags.enemyGodMode,
          damageMultiplier: effectiveMult,
          stealthAttack: this.player.combat.stealthAttack,
          upgradeResolver: this.upgradeResolver,
        },
      );

      if (result) {
        // Record hit for simulation bridge (no-op when ?sim=1 is absent)
        const bridge = SimulationBridge.getInstance();
        if (bridge) {
          const targetTag = bridge.getTagForEnemy(enemy) ?? enemy.instanceTag;
          bridge.recordHit({
            timestamp: Date.now(),
            attackType,
            targetTag,
            damage: result.damage,
            isCrit: result.isCrit,
            isKill: enemy.healthComponent.isDead,
          });
        }

        enemy.ai.spikeAggro(this.player.sprite.x, this.player.sprite.y);

        // Dash-attack stamina refund (Adrenaline passive)
        if (this.player.combat.isDashAttack) {
          this.player.stamina.applyDashAttackRefund();
        }

        this.knockbackSystem.apply(
          enemy,
          attackType,
          hitDir
        );

        // Resolve stagger before crawl push so the tag appears on the hit line
        let staggered = false;
        const staggerMs = ConfigLoader.getStaggerDurationMs(attackType);
        const upgradeWon = upgradeStagger && Math.random() < upgradeStagger.chance;
        const effectiveStagger = upgradeWon
          ? Math.max(staggerMs, upgradeStagger!.durationMs)
          : staggerMs;
        if (effectiveStagger > 0 && !(enemy.statusEffectTracker?.isImmune('stagger'))) {
          staggered = enemy.ai.applyStagger(effectiveStagger);
          if (staggered && enemy.statusEffectTracker) {
            // Bridge melee stagger into the tracker so it records the effect.
            // Set _bridgingFromMelee to prevent the onCCApplied callback from
            // re-applying stagger to the AI (which already has it).
            enemy.statusEffectTracker._bridgingFromMelee = true;
            try {
              enemy.statusEffectTracker.apply('stagger', {
                durationMs: effectiveStagger,
                sourceSkillId: attackType,
              });
            } finally {
              enemy.statusEffectTracker._bridgingFromMelee = false;
            }
            this.spawnStaggerLabel(enemy);
          }
        }

        const extras = [
          result.isCrit ? 'CRIT' : '',
          result.isBackstab ? 'BACKSTAB' : '',
          staggered ? 'STAGGER' : '',
          this.player.combat.isDashAttack ? 'DASH ATTACK' : '',
        ].filter(Boolean).join(' ');
        DebugLog.log('combat', `HIT ${enemy.config.id} for ${result.damage} dmg${extras ? ` (${extras})` : ''} [${result.attackType}]`);

        // Crawl overlay — hit event
        this.pushCrawl(
          `Hit ${enemy.config.id} for ${result.damage} dmg${extras ? ` (${extras})` : ''}`,
          result.isCrit ? '#ffcc00' : '#cccccc',
        );

        // Award Grit tree XP for each melee hit (attack type → skill ID via registry)
        this.player.skillProgression.recordUse(xpSkillId, {
          danger: this.getDanger(),
          toughEnemy: TOUGH_WEIGHTS.has(enemy.config.weight),
          hitLanded: true,
          backstab: result.isBackstab,
          stealthAttack: this.player.combat.stealthAttack,
          multiHit: this.swingHitCount >= 2,
          killSecured: enemy.healthComponent.isDead,
          dangerPresent: this.getDanger() > 0,
        });

        this.damageNumbers.spawn(
          enemy.sprite.x, enemy.sprite.y, result.damage, result.baseDamage, result.isCrit, result.isBackstab,
        );

        // One-hit kill (only if enemy god mode is off)
        if (DebugConfig.flags.oneHitKill && !DebugConfig.flags.enemyGodMode
            && !enemy.healthComponent.isDead) {
          enemy.healthComponent.takeDamage(enemy.healthComponent.hp);
        }

        // Log kill (removal handled by EnemyManager.sweepDeadEnemies via entity-died event)
        if (enemy.healthComponent.isDead) {
          DebugLog.log('combat', `KILLED ${enemy.config.id}`);
          this.pushCrawl(`Killed ${enemy.config.id}!`, '#ff4444');
        }

        // Combat sound alert — nearby enemies hear the hit (no LOS required)
        this.enemyManager.broadcastCombatAlert(
          enemy.sprite.x, enemy.sprite.y,
          COMBAT_ALERT_AGGRO_AMOUNT, COMBAT_ALERT_RADIUS,
        );
      }
    }
  }

  /** True if any living enemy is within the miss-proximity radius of the player. */
  private anyEnemyInMissRadius(): boolean {
    const radius = ConfigLoader.combat.damageNumbers.missProximityRadius;
    const radiusSq = radius * radius;
    const px = this.player.sprite.x;
    const py = this.player.sprite.y;

    for (const enemy of this.enemyManager.all) {
      if (enemy.healthComponent.isDead) continue;
      if (distanceSq(px, py, enemy.sprite.x, enemy.sprite.y) <= radiusSq) return true;
    }
    return false;
  }

  /** Wire the Phaser overlap collider between projectiles and the player sprite. */
  setupProjectileOverlap(scene: Phaser.Scene, projectilePool: ProjectilePool): void {
    scene.physics.add.overlap(
      projectilePool.group,
      this.player.sprite,
      (projObj) => {
        const projRect = projObj as Phaser.GameObjects.Rectangle;
        const info = projectilePool.getDamageForRect(projRect);
        if (!info) return;
        const hitDir = normalize(
          this.player.sprite.x - projRect.x,
          this.player.sprite.y - projRect.y,
        );
        const projDR = this.getPlayerDamageReduction();
        const projDrMult = projDR > 0 ? (1 - projDR) : 1;
        const result = this.combatSystem.applyProjectileHit(
          this.player, info.damage, info.attackType, hitDir,
          { godMode: DebugConfig.flags.playerGodMode, damageMultiplier: projDrMult !== 1 ? projDrMult : undefined },
        );
        if (result) {
          this.damageNumbers.spawn(this.player.sprite.x, this.player.sprite.y, result.damage, result.baseDamage, result.isCrit, false, true);
          this.screenEffects.playerHitEffect();
        }
        projectilePool.deactivateByRect(projRect);
      },
    );
  }

  /** Check each enemy's active skill for damage-phase hits against the player, applying DR and knockback. */
  checkEnemyAttackHits(): void {
    for (const enemy of this.enemyManager.all) {
      if (enemy.healthComponent.isDead) continue;
      // Only deal damage during skill's active damage phase, once per activation
      const skill = enemy.ai.activeSkill;
      if (!skill?.isInDamagePhase()) continue;
      if (skill.hitApplied) continue;

      // Read damage info from the skill itself (ADR-033)
      const damageInfo = skill.getDamageInfo();
      if (!damageInfo) continue;

      // Check if player is within the skill's damage range (area skills use zone center)
      const srcX = damageInfo.centerX ?? enemy.sprite.x;
      const srcY = damageInfo.centerY ?? enemy.sprite.y;
      const dist = distance(srcX, srcY, this.player.sprite.x, this.player.sprite.y);
      if (dist > damageInfo.range) continue;

      const hitDir = normalize(
        this.player.sprite.x - srcX,
        this.player.sprite.y - srcY,
      );

      // Apply damage to player (Officer aura buff × player DR)
      const incomingMult = this.buffManager.getDamageMultiplier(enemy);
      const playerDR = this.getPlayerDamageReduction();
      const drMult = playerDR > 0 ? (1 - playerDR) : 1;
      const combinedIncoming = incomingMult * drMult;

      const result = this.combatSystem.applyHit(
        enemy, this.player,
        damageInfo.attackType,
        hitDir,
        { godMode: DebugConfig.flags.playerGodMode, damageMultiplier: combinedIncoming !== 1.0 ? combinedIncoming : undefined }
      );

      if (result) {
        skill.markHitApplied();
        DebugLog.log('combat', `TOOK ${result.damage} dmg from ${enemy.config.id} [${damageInfo.attackType}] player=(${this.player.sprite.x.toFixed(0)},${this.player.sprite.y.toFixed(0)}) enemy=(${enemy.sprite.x.toFixed(0)},${enemy.sprite.y.toFixed(0)}) dist=${dist.toFixed(0)}`);
        this.damageNumbers.spawn(
          this.player.sprite.x, this.player.sprite.y, result.damage, result.baseDamage, result.isCrit, result.isBackstab, true,
        );
        this.screenEffects.playerHitEffect();
        this.pushCrawl(
          `${enemy.config.id} hits you for ${result.damage} dmg`,
          '#cc4444',
        );

        // Push player away from attacker (controlled knockback, handles wall contact)
        this.knockbackSystem.apply(
          this.player,
          damageInfo.attackType,
          hitDir,
        );

        // Combat sound alert — nearby enemies hear the hit (no LOS required)
        this.enemyManager.broadcastCombatAlert(
          this.player.sprite.x, this.player.sprite.y,
          COMBAT_ALERT_AGGRO_AMOUNT, COMBAT_ALERT_RADIUS,
        );
      }
    }
  }

  /** Compute the player's total damage reduction fraction from all sources, capped at 0.9. */
  private getPlayerDamageReduction(): number {
    // Source-keyed DR (Petrify, etc.)
    let dr = this.player.playerState.damageReduction;

    // Status-effect-based DR (Force Bind / Grapple / Crush clinch DR, Ironbody stance)
    const tracker = this.player.statusEffectTracker;
    if (tracker) {
      const forceBind = tracker.get('grit_force_bind_dr');
      if (forceBind) dr += forceBind.intensity;
      const ironbody = tracker.get('ironbody_active');
      if (ironbody) dr += ironbody.intensity;
    }

    return Math.min(dr, 0.9);
  }

  /** Spawn a "STAGGERED" floaty label above the enemy. */
  private spawnStaggerLabel(enemy: Enemy): void {
    const cfg = ConfigLoader.combat.damageNumbers;
    this.damageNumbers.spawnLabel(
      enemy.sprite.x, enemy.sprite.y,
      'STAGGERED', cfg.staggerColor, `${cfg.baseFontSizePx}px`,
    );
  }
}
